import React, { useRef, useEffect, useState } from "react";
import "./BlogPage.scss";
import { getBlogsList, getFeaturedBlogsList } from "services/home/blogs";
import { Link, useNavigate } from "react-router-dom";

const BlocksPage = () => {
  const [blogs, setBlogs] = useState([]);
  const [featuredBlogs, setFeaturedBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentSlide, setCurrentSlide] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [isFetching, setIsFetching] = useState(false);
  const postsPerPage = 5;
  const scrollRef = useRef(null);
  const navigate = useNavigate();

  const [isAtTop, setIsAtTop] = useState(true);
  const [isAtBottom, setIsAtBottom] = useState(true);
  const [loadMoreClicked, setLoadMoreClicked] = useState(false);

  const slugify = (text) =>
    text
      .toString()
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "-")
      .replace(/[^\w-]+/g, "")
      .replace(/--+/g, "-");


  // ✅ Scroll arrow logic
  useEffect(() => {
    const handleScroll = () => {
      if (!scrollRef.current) return;
      const { scrollTop, scrollHeight, clientHeight } = scrollRef.current;
      setIsAtTop(scrollTop <= 5);
      setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 5);
    };

    const container = scrollRef.current;
    if (container) {
      container.addEventListener("scroll", handleScroll);
      handleScroll();
      return () => container.removeEventListener("scroll", handleScroll);
    }
  }, [loadMoreClicked]);

  const scrollUp = () =>
    scrollRef.current?.scrollBy({ top: -300, behavior: "smooth" });
  const scrollDown = () =>
    scrollRef.current?.scrollBy({ top: 300, behavior: "smooth" });

  // ✅ Fetch all blogs
  const fetchBlogs = async (page = 1) => {
    try {
      setIsFetching(true);
      const res = await getBlogsList({ page, limit: postsPerPage });

      if (res.code === 200 && res.data?.blogs) {
        let newBlogs = res.data.blogs || [];
        setBlogs((prev) => {
          const combined = page === 1 ? newBlogs : [...prev, ...newBlogs];
          const uniqueBlogs = combined.filter(
            (blog, index, self) =>
              self.findIndex((b) => b.ID === blog.ID) === index
          );
          return uniqueBlogs.sort(
            (a, b) => (a.DisplayOrder || 999) - (b.DisplayOrder || 999)
          );
        });
        const totalPages = res.data.totalPages || 1;
        setHasMore(page < totalPages);
      }
    } catch (err) {
      console.error("Error fetching blogs:", err);
    } finally {
      setLoading(false);
      setIsFetching(false);
    }
  };

  // ✅ Fetch featured blogs
  const fetchFeaturedBlogs = async () => {
    try {
      const res = await getFeaturedBlogsList();
      if (res.code === 200 && res.data?.blogs) {
        setFeaturedBlogs(res.data.blogs);
      }
    } catch (err) {
      console.error("Error fetching featured blogs:", err);
    }
  };

  useEffect(() => {
    fetchBlogs(currentPage);
    fetchFeaturedBlogs();
  }, []);

  // ✅ Auto-slide for latest posts
  useEffect(() => {
    if (blogs.length === 0) return;
    const interval = setInterval(() => {
      setCurrentSlide((prev) => (prev + 1) % blogs.length);
    }, 4000);
    return () => clearInterval(interval);
  }, [blogs.length]);

  // ✅ Load More
  const loadMorePosts = async () => {
    if (isFetching || !hasMore) return;
    const nextPage = currentPage + 1;
    await fetchBlogs(nextPage);
    setCurrentPage(nextPage);
    setLoadMoreClicked(true);
  };

  if (loading)
    return (
      <div className="loader-wrapper">
        <div className="loader"></div>
      </div>
    );

  if (blogs.length === 0)
    return (
      <div className="loader-wrapper">
        <div>No blogs found.</div>
      </div>
    );

  const firstBlog = blogs[0];
  const otherBlogs = blogs.slice(1);

  return (
    <div className="blocks-page">
      {/* Banner */}
      <div className="banner">
        <h1 className="banner-title">
          Power Your Prep with Insightful Blog Articles
        </h1>
        <p className="banner-subtitle">
          Discover Winning Strategies in Our Blog Series
        </p>
      </div>

      {/* Main Content */}
      <div className="content">
        {/* Left Section */}
        <div className="content-left">
          <div className="sectionfirst">
            <div className="sectionfirst-card">
              <h4 className="sectionfirst-card-title">
                Want to be aware of everything?
              </h4>
            </div>
          </div>
          <br />

          {/* Main Post */}
          <div className="post">
            <img
              src={firstBlog.attachmentUrl}
              alt={firstBlog.Title}
              className="post-image"
            />
            <div className="post-details">
              <h4 className="post-title">
                <Link to={`/blog/${slugify(firstBlog.Title)}`} state={{ blog: firstBlog }}>
                  {firstBlog.Title}
                </Link>
              </h4>
              <p className="post-content">{firstBlog.ShortDescription}</p>
            </div>
          </div>

          {/* Other Blogs */}
          <div className="other-posts-wrapper">
            <div className="other-posts" ref={scrollRef}>
              {otherBlogs.map((b) => (
                <div key={b.ID} className="small-post">
                  <img
                    src={b.attachmentUrl}
                    alt={b.Title}
                    className="small-post-image"
                  />
                  <div className="post-details">
                    <h4 className="post-title">
                      <Link to={`/blog/${slugify(b.Title)}`} state={{ blog: b }}>

                        {b.Title}
                      </Link>
                    </h4>
                    <p className="post-content">{b.ShortDescription}</p>
                  </div>
                </div>
              ))}
            </div>

            {loadMoreClicked && (
              <>
                {!isAtTop && (
                  <button className="scroll-arrow up" onClick={scrollUp}>
                    ↑
                  </button>
                )}
                {!isAtBottom && (
                  <button className="scroll-arrow down" onClick={scrollDown}>
                    ↓
                  </button>
                )}
              </>
            )}
          </div>

          {hasMore && (
            <div className="pagination-btn-load">
              <button
                onClick={loadMorePosts}
                disabled={isFetching}
                className="pagination-btn-load-more"
              >
                {isFetching ? "Loading..." : "Load More"}
              </button>
            </div>
          )}
        </div>

        {/* Right Section */}
        <div className="content-right">
          {/* ✅ Featured Blogs Section */}
          <div className="featuredposts">
            <h4 className="featuredposts-heading">Featured Blogs</h4>
            <hr />

            {featuredBlogs.length > 0 ? (
              featuredBlogs.slice(0, 4).map((blog) => (
                <Link
                  key={blog.ID}
                  to={`/blog/${slugify(blog.Title)}`}

                  state={{ blog }}
                  style={{ textDecoration: "none", color: "inherit" }}
                >
                  <div
                    className="featuredpost"
                    style={{
                      display: "flex",
                      gap: "12px",
                      cursor: "pointer",
                    }}
                  >
                    <img
                      src={blog.attachmentUrl}
                      alt={blog.Title}
                      className="w-20 h-20 object-cover rounded-md"
                    />
                    <div className="featuredpost-text">
                      <p className="featuredpost-text-heading">{blog.Title}</p>
                      <p className="featuredpost-text-content">
                        {blog.ShortDescription}
                      </p>
                    </div>
                  </div>
                </Link>
              ))
            ) : (
              <p className="no-featured-blog-text text-center py-4">
                No featured blogs available at this time.
              </p>
            )}
          </div>

          {/* ✅ Latest Posts Slider */}
          <div className="latest-posts">
            <h4 className="latest-posts-heading">Latest Posts</h4>
            <hr />
            <div
              className="latest-post-slide"
              onClick={() =>
                navigate(`/blog/${slugify(blogs[currentSlide].Title)}`, {

                  state: { blog: blogs[currentSlide] },
                })
              }
            >
              <img
                src={blogs[currentSlide]?.attachmentUrl}
                alt={blogs[currentSlide]?.Title}
                className="latest-post-image "
              />
              <div className="latest-post-details">
                <h5 className="latest-post-title">
                  {blogs[currentSlide]?.Title}
                </h5>
                <p className="latest-post-content">
                  {blogs[currentSlide]?.ShortDescription}
                </p>
              </div>
            </div>
          </div>

          {/* ✅ Join Community */}
          <div className="join-community">
            <h4 className="sidebar-heading">Join the Community</h4>
            <hr />
            <h5>Sahyog Care</h5>
            <a
              href="#"
              onClick={(e) => e.preventDefault()}
              target="_blank"
              className="telegram-btn"
              rel="noopener noreferrer"
            >
              <div className="icon-circle">
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/8/82/Telegram_logo.svg"
                  alt="Telegram"
                />
              </div>
              Join our Telegram Channel
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlocksPage;
